import type { MessengerPlatform } from "@carry/messenger-platform";
import { isDriverCapableRole } from "@carry/shared";

export const SESSION_STORAGE_KEY = "carry.driver.session";

export interface DriverSessionUser {
  id: string;
  role: "admin" | "office_staff" | "driver";
  status: "pending" | "active" | "inactive";
  name: string;
  email: string | null;
  line_linked: boolean;
}

/**
 * driver アプリの利用を許可するロール判定。admin・office_staff(経理/事務) は運用上ドライバーとして
 * 稼働するためログインできる。判定の単一源泉は `@carry/shared` の `DRIVER_CAPABLE_ROLES`。
 */
export function isDriverAppRole(role: DriverSessionUser["role"]): boolean {
  return isDriverCapableRole(role);
}

/**
 * 認証は httpOnly Cookie (Better-Auth セッション) で行うため、セッショントークンは
 * JS から保持しない (XSS 窃取対策)。localStorage には表示・状態判定用の非機微な
 * user と有効期限のみを保存し、API 呼び出しは `credentials: 'include'` で Cookie を送る。
 */
export interface StoredDriverSession {
  expiresAt: string;
  user: DriverSessionUser;
}

export type SignInResult =
  | { ok: true; expiresAt: string; user: DriverSessionUser }
  | { ok: false; error: string; message?: string; retryAfterSec?: number };

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SignInInput {
  platform: MessengerPlatform;
  apiBaseUrl: string;
  fetchFn?: typeof fetch;
  storage?: StorageLike | null;
}

export async function signInWithMessenger(input: SignInInput): Promise<SignInResult> {
  const { platform, apiBaseUrl } = input;
  const fetchFn = input.fetchFn ?? fetch;
  const accessToken = await platform.getAccessToken();
  const path = "/api/v1/auth/sign-in/liff";
  const res = await fetchFn(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token: accessToken }),
    // サーバーが Set-Cookie する httpOnly セッション Cookie を受け取り、以降のリクエストで送る。
    credentials: "include",
  });
  const body = (await res.json().catch(() => ({}))) as {
    data?: { user: DriverSessionUser; session: { expires_at: string } };
    error?: string;
    message?: string;
    retryAfterSec?: number;
  };

  if (!res.ok || !body.data) {
    return {
      ok: false,
      error: body.error ?? "SIGN_IN_FAILED",
      message: body.message,
      retryAfterSec: body.retryAfterSec,
    };
  }

  // トークンは Cookie が保持するため localStorage には保存しない。
  // 表示・ロール判定用の user と有効期限のみ保存する。
  const stored: StoredDriverSession = {
    expiresAt: body.data.session.expires_at,
    user: body.data.user,
  };
  if (input.storage !== null) {
    const storage = input.storage ?? getDefaultStorage();
    storage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
  }
  return {
    ok: true,
    expiresAt: body.data.session.expires_at,
    user: body.data.user,
  };
}

export function readStoredDriverSession(storage?: StorageLike | null): StoredDriverSession | null {
  const store = storage ?? getDefaultStorage();
  if (!store) return null;
  const raw = store.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredDriverSession;
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() <= Date.now()) {
      store.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredDriverSession(storage?: StorageLike | null) {
  const store = storage ?? getDefaultStorage();
  store?.removeItem(SESSION_STORAGE_KEY);
}

/**
 * 現在の httpOnly Cookie セッションが有効かを軽量に確認する (GET /api/v1/auth/me)。
 * sign-in (LINE トークン検証 + セッション発行) より大幅に軽いため、起動時に有効な Cookie が
 * あれば再サインインを省略でき、読込時間を減らせる。Cookie は credentials:'include' で送る。
 */
export async function verifyDriverSession(input: {
  apiBaseUrl: string;
  fetchFn?: typeof fetch;
}): Promise<boolean> {
  const fetchFn = input.fetchFn ?? fetch;
  try {
    const res = await fetchFn(`${input.apiBaseUrl.replace(/\/$/, "")}/api/v1/auth/me`, {
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

function getDefaultStorage(): StorageLike | null {
  if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
    return (globalThis as unknown as { localStorage: StorageLike }).localStorage;
  }
  return null;
}
